/**
 * "@" in a conversation (conversation-members spec): typing "@" lists the conversation's members — people, personas,
 * employees, external agents — and picking one writes "@<name> " into the message and adds that member to who the
 * message is for (the composer's "To"). The address goes out by id; a name is never read back out of the typed text.
 * Pure helpers; the panel (`MentionPanel.tsx`) and the keys (`useMentionKeys.ts`) use them.
 */
import type { ConversationMemberRef } from '@confluent/shared/lib/conversation-addressing';

/** Names listed at once. */
const LISTED = 8;
/** A query longer than any name is no longer a mention being typed. */
const QUERY_MAX = 60;

/** The "@name" being typed at the end of the message: the text after "@" and where the "@" is; null when none is. */
export function mentionQuery(text: string): { query: string; start: number } | null {
  const start = text.lastIndexOf('@');
  if (start < 0) return null;
  // "a@b" is an address, not a mention.
  if (start > 0 && !/\s/.test(text.charAt(start - 1))) return null;
  const query = text.slice(start + 1);
  if (query.includes('\n') || query.length > QUERY_MAX) return null;
  return { query, start };
}

/** The members whose name the query starts, then those it appears in, by name. */
export function matchingMembers<T extends Pick<ConversationMemberRef, 'name'>>(
  members: readonly T[],
  query: string,
): T[] {
  const wanted = query.toLowerCase();
  const byName = [...members].sort((a, b) => a.name.localeCompare(b.name));
  const leading = byName.filter((member) => member.name.toLowerCase().startsWith(wanted));
  const inside = byName.filter((member) => !leading.includes(member) && member.name.toLowerCase().includes(wanted));
  return [...leading, ...inside].slice(0, LISTED);
}

/** The message with the "@query" at `start` replaced by the picked member's name. */
export function insertMention(text: string, start: number, member: Pick<ConversationMemberRef, 'name'>): string {
  return `${text.slice(0, start)}@${member.name} `;
}
